// apps/onboarding/src/components/Layout.tsx
import React, { useState, useMemo } from 'react';
import { useTranslation } from '@tasama/i18n';
import { BusinessAdvisorFeature } from '../../features/businessAdvisor/BusinessAdvisorFeature';
import { useCompanyTypeStore } from '../../features/company-type/stores/company-type-store';
import { mapCompanyTypeForApi } from '@tasama/business-advisor';

import { Layout as LayoutTemplate } from '@tasama/ui';
import { CompanyTypePage } from '../../features/company-type/pages/CompanyTypePage';
import ServiceNeeds from '../../features/service-needs/pages/ServiceNeeds';
import Confirmation from '../../features/confirmation/pages/Confirmation';
import { YourDetailsPage } from '../../features/your-details/pages/YourDetailsPage';
import CompanyDetailsPage from '../../features/company-details/pages/CompanyDetailsPage';
import { UploadDocumentsPage } from '../../features/upload-documents/pages/UploadDocumentsPage';
import { ProjectDetailsPage } from '../../features/project-details/pages/ProjectDetailsPage';
import { useOnboardingApi } from '../hooks/use-onboarding-api.hook';
import { useOnboardingSteps } from '../hooks/use-onboarding-steps.hook';
import { useStepValidation } from '../hooks/use-step-validation.hook';
import { useOnboardingPayload } from '../hooks/use-onboarding-payload.hook';
import { useOnboardingNavigation } from '../hooks/use-onboarding-navigation.hook';
import type { LayoutProps } from '@tasama/ui';
import { FeatureFlags, isEnabled } from '@tasama/config';
import { env } from '../config/env';

export const Layout: React.FC = () => {
  const { t } = useTranslation(['onboarding', 'common']);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [modalContent, setModalContent] = useState('');

  const {
    submitStepData,
    isSubmitting,
    error: apiError,
    clearError,
  } = useOnboardingApi();
  const { getCurrentStepFormData } = useOnboardingPayload();
  const { stepValidation } = useStepValidation();

  const {
    internalStep,
    handleContinue,
    handleBack,
    handleDone,
    isContinueDisabled,
    isPending,
  } = useOnboardingNavigation({
    stepValidation,
    submitStepData,
    clearError,
    isSubmitting,
    getCurrentStepFormData,
  });

  const {
    stepRoutes,
    stepsWithCompletion,
    currentStepIndex,
    isConfirmationScreen,
  } = useOnboardingSteps(internalStep);

  const errorConfig = useMemo(() => {
    if (!apiError) {
      return { show: false };
    }

    return {
      show: true,
      data: {
        type: apiError.type || 'error',
        message: apiError.message,
        onClear: clearError,
      },
    };
  }, [apiError, clearError]);

  const layoutConfig: LayoutProps = {
    variant: 'onboarding',
    navigation: {
      showLanguageSwitcher: isEnabled(FeatureFlags.LANGUAGE_SWITCHER),
      loginUrl: env.PORTAL_APP_BASE_URL,
    },
    stepper: {
      routes: stepRoutes,
      steps: stepsWithCompletion,
      validation: stepValidation,
      currentStepIndex: currentStepIndex,
      onStepClick: (_stepIndex: number, _stepData?: any) => {
        // Step click handler
      },
      onStartOnboarding: () => {
        // Start onboarding handler
      },
    },
    content: {
      outletContext: {
        setIsModalOpen: (open: boolean) => setIsModalOpen(open),
        setModalContent: (content: string) => setModalContent(content),
      },
    },
    actions: {
      onContinue: handleContinue,
      onBack: handleBack,
      onDone: handleDone,
      backText: t('common:actions.previous'),
      continueText:
        internalStep === 'service-needs'
          ? t('common:actions.submit')
          : t('common:actions.next'),
      doneText: t('common:actions.done'),
      isContinueLoading: isPending || isSubmitting,
      isContinueDisabled: isContinueDisabled,
      isBackDisabled: currentStepIndex === 0,
      isDoneDisabled: isPending || isSubmitting,
      isDoneLoading: isPending || isSubmitting,
      showDoneButton: isConfirmationScreen,
    },
    layout: {
      maxWidth: 'max-w-container-3xl w-full',
      padding: 'px-4 pt-5 md:px-6 md:pt-6 lg:px-[62px] lg:pt-6',
      showBottomNavigation: true,
      showSidebar: true,
    },
    modal: {
      isOpen: isModalOpen,
      onClose: () => setIsModalOpen(false),
      title: t('termsAndConditions.title'),
      content: modalContent,
      iconVariant: 'brand',
      headerType: 'left',
      className: 'max-h-[80vh] overflow-y-auto whitespace-pre-line',
    },
    error: errorConfig,
    testID: 'onboarding-layout',
  };

  const renderStepComponent = () => {
    const stepComponentMap: Record<string, React.ReactNode> = {
      'company-type': <CompanyTypePage />,
      'your-details': <YourDetailsPage />,
      'upload-documents': <UploadDocumentsPage />,
      'company-details': <CompanyDetailsPage />,
      'project-details': <ProjectDetailsPage />,
      'service-needs': <ServiceNeeds />,
      confirmation: <Confirmation />,
    };
    return stepComponentMap[internalStep] || <CompanyTypePage />;
  };

  const { formData: companyTypeFormData } = useCompanyTypeStore();
  const companyType = mapCompanyTypeForApi(
    companyTypeFormData?.companyType,
    companyTypeFormData?.companyStatus
  );
  const stepperStore = { currentStep: { id: internalStep } };

  return (
    <>
      <LayoutTemplate {...layoutConfig}>{renderStepComponent()}</LayoutTemplate>
      <BusinessAdvisorFeature
        stepperStore={stepperStore}
        companyType={companyType}
      />
    </>
  );
};
